import { foldForSearch, SearchableValue } from './searchHelper'
import { SearchableText } from './textHelper'

export type SnippetSegment = { text: string; match: boolean }

const DEFAULT_RADIUS = 140
const DEFAULT_VALUE_LENGTH = 100
/** How far to look for a space before giving up and cutting mid-word. */
const WORD_BOUNDARY_SLACK = 20

const ELLIPSIS: SnippetSegment = { text: '…', match: false }

const snapStart = (text: string, index: number): number => {
   if (index === 0) return 0
   const space = text.lastIndexOf(' ', index)
   return space >= 0 && index - space <= WORD_BOUNDARY_SLACK ? space + 1 : index
}

const snapEnd = (text: string, index: number): number => {
   if (index >= text.length) return text.length
   const space = text.indexOf(' ', index)
   return space >= 0 && space - index <= WORD_BOUNDARY_SLACK ? space : index
}

/** Splits [start, end) into alternating plain and matched runs. */
const markMatches = (text: string, lower: string, query: string, start: number, end: number): SnippetSegment[] => {
   const segments: SnippetSegment[] = []
   const push = (value: string, match: boolean) => {
      if (value) segments.push({ text: value, match })
   }

   let cursor = start
   let at = lower.indexOf(query, start)
   while (at >= 0 && at < end) {
      const matchEnd = Math.min(at + query.length, end)
      push(text.slice(cursor, at), false)
      push(text.slice(at, matchEnd), true)
      cursor = matchEnd
      at = lower.indexOf(query, cursor)
   }
   push(text.slice(cursor, end), false)

   return segments
}

/**
 * Null means the query isn't in this text, not that the row isn't a hit — highlighting is decoration
 * only. The length guard catches locale lowercasing that changes length (İ becomes i + U+0307), where
 * indices into `lower` would no longer line up with `text`.
 */
export const buildSnippet = (source: SearchableText, normalizedQuery: string, radius: number = DEFAULT_RADIUS): SnippetSegment[] | null => {
   const { text, lower } = source
   if (!text || !normalizedQuery || text.length !== lower.length) return null

   const first = lower.indexOf(normalizedQuery)
   if (first < 0) return null

   const lead = Math.floor(radius / 3)
   // Never let the word snap push the window past the match it is supposed to be centred on.
   const start = Math.min(snapStart(text, Math.max(0, first - lead)), first)
   const end = snapEnd(text, Math.min(text.length, start + radius))

   return [
      ...(start > 0 ? [ELLIPSIS] : []),
      ...markMatches(text, lower, normalizedQuery, start, end),
      ...(end < text.length ? [ELLIPSIS] : []),
   ]
}

/** Short values are marked whole; longer ones fall back to a window so a late match still shows. */
export const highlightValue = (
   value: SearchableValue,
   normalizedQuery: string,
   maxLength: number = DEFAULT_VALUE_LENGTH
): SnippetSegment[] | null => {
   const text = foldForSearch(value)
   if (!text || !normalizedQuery) return null

   const lower = text.toLocaleLowerCase('sv')
   if (text.length !== lower.length || lower.indexOf(normalizedQuery) < 0) return null

   return text.length <= maxLength ? markMatches(text, lower, normalizedQuery, 0, text.length) : buildSnippet({ text, lower }, normalizedQuery, maxLength)
}

export const textHead =(source: SearchableText, length: number = DEFAULT_RADIUS * 2): string => {
   const { text } = source
   if (!text) return ''
   if (text.length <= length) return text
   return `${text.slice(0, snapEnd(text, length))}…`
}
